import { useEffect, useState } from "react"

import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"
import { agents, type AgentId } from "@/lib/agents"

// "Works with any agent": four agent marks in a row that steps right to left.
// Every tick the row slides one slot: the leftmost mark shrinks and fades out
// while a new one scales and fades in at the fourth slot (no clipping mask).
// The move resolves on a timer rather than transitionend so a hidden tab
// can't leave it stuck mid-slide.
const ORDER: AgentId[] = [
  "claude",
  "codex",
  "gemini",
  "copilot",
  "cursor",
  "amp",
  "opencode",
  "droid",
  "pi",
  "antigravity",
]
const VISIBLE = 4
const SLOT_PX = 16
const GAP_PX = 12
const STEP_MS = 2000
const MOVE_MS = 520

const stepDistance = SLOT_PX + GAP_PX
const viewportWidth = VISIBLE * SLOT_PX + (VISIBLE - 1) * GAP_PX
// The marks are the site's monochrome agent logos (see AgentCompatibilitySection):
// flattened to black, inverted in dark mode, and dimmed to the design's grey.
const MARK_OPACITY = 0.45
const HIDDEN_SCALE = 0.4

// The leaving mark (index 0) and the arriving one (index VISIBLE) reveal and
// vanish through scale and opacity; the marks between just ride the slide.
function markStyle(index: number, moving: boolean) {
  const shown = { opacity: MARK_OPACITY, transform: "scale(1)" }
  const hidden = { opacity: 0, transform: `scale(${HIDDEN_SCALE})` }
  const transition = moving
    ? `opacity ${MOVE_MS}ms ease-out, transform ${MOVE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
    : "none"
  if (index === 0) return { ...(moving ? hidden : shown), transition }
  if (index === VISIBLE) return { ...(moving ? shown : hidden), transition }
  return shown
}

export function AgentLogoCarousel() {
  const [start, setStart] = useState(0)
  const [moving, setMoving] = useState(false)

  const prefersReducedMotion = usePrefersReducedMotion()
  useEffect(() => {
    if (prefersReducedMotion) return

    let settle: number | null = null
    const interval = window.setInterval(() => {
      setMoving(true)
      settle = window.setTimeout(() => {
        setStart((current) => (current + 1) % ORDER.length)
        setMoving(false)
      }, MOVE_MS)
    }, STEP_MS)

    return () => {
      window.clearInterval(interval)
      if (settle !== null) window.clearTimeout(settle)
    }
  }, [prefersReducedMotion])

  // The four visible marks plus the one waiting just outside the right edge.
  const shown = Array.from({ length: VISIBLE + 1 }, (_, index) => {
    const agent = agents[ORDER[(start + index) % ORDER.length]!]
    return { ...agent, key: `${start}-${index}` }
  })

  return (
    <span
      role="img"
      aria-label={`${shown
        .slice(0, VISIBLE)
        .map((agent) => agent.label)
        .join(", ")} and more`}
      className="block shrink-0"
      style={{ width: viewportWidth }}
    >
      <span
        className="flex items-center"
        style={{
          gap: GAP_PX,
          transform: moving ? `translateX(-${stepDistance}px)` : "none",
          transition: moving ? `transform ${MOVE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)` : "none",
        }}
      >
        {shown.map((agent, index) => (
          <img
            key={agent.key}
            src={agent.logoUrl}
            alt=""
            width={SLOT_PX}
            height={SLOT_PX}
            className="size-4 shrink-0 brightness-0 dark:invert"
            style={markStyle(index, moving)}
          />
        ))}
      </span>
    </span>
  )
}
